import { useEffect } from 'react';
import { useSnackbars } from '@customerio/pluma-components/react';
import { useAnalytics } from '@/services/analytics';
import { getApiClientErrorDetailFromError } from '@/services/api';
import { isEmptyString } from '@/utils/string';
import { WAREHOUSE_DESTINATION_CONFIG_KEYS, type WarehouseDestination } from '@/services/db/warehouse-sync/entity';
import { useCreateWarehouseSync, useValidateWarehouseConfig } from '@/services/db/warehouse-sync/mutations';
import type { WarehouseSyncCreateInput } from '@/services/db/warehouse-sync/mutations';
import type { BucketConfigDraft } from './components/provider-form';
import type { ExportSelection } from './components/select-data';

type UseCreateSyncArgs = {
  workspaceId: string;
  destination: WarehouseDestination;
  destinationName: string;
  onCreated: () => void;
};

type SyncDraft = {
  config: BucketConfigDraft;
  selection: ExportSelection;
};

/**
 * The create body: every export toggle plus exactly one storage block.
 *
 * No `update_type` — that field only exists on updates. The server assigns the
 * id, timestamp and schema version, so none of them are sent.
 */
function buildCreateInput(
  destination: WarehouseDestination,
  { config, selection }: SyncDraft,
): WarehouseSyncCreateInput {
  return {
    ...selection,
    [WAREHOUSE_DESTINATION_CONFIG_KEYS[destination]]: config,
  };
}

/** The bucket a sync writes to. Azure has no bucket name, so its path stands in. */
function bucketLabel(config: BucketConfigDraft): string {
  return isEmptyString(config.bucket_name) ? (config.bucket_path ?? '') : config.bucket_name;
}

export function useCreateSync({ workspaceId, destination, destinationName, onCreated }: UseCreateSyncArgs) {
  const snackbars = useSnackbars();
  const { track } = useAnalytics();

  const validateConfig = useValidateWarehouseConfig({ workspaceId: Number(workspaceId) });
  const createSync = useCreateWarehouseSync({ workspaceId: Number(workspaceId) });

  // Ember reports this on route entry; the funnel needs a start per completion.
  useEffect(() => {
    void track('warehouse', 'createSyncStarted', { Type: destinationName });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per wizard, not per render
  }, [destination]);

  /**
   * Ask the provider whether these credentials work. Resolves `false` on a
   * rejection rather than throwing, so the caller just stays on the step.
   */
  const validate = async (draft: SyncDraft): Promise<boolean> => {
    try {
      await validateConfig.mutateAsync({ sync: buildCreateInput(destination, draft) });
      return true;
    } catch (error) {
      // This endpoint's whole value is its per-provider detail ("bucket not found",
      // "access denied") — a generic line would leave the user guessing at the one
      // step that blocks the flow.
      const detail = getApiClientErrorDetailFromError(error);
      snackbars.create({
        message: isEmptyString(detail)
          ? `We could not connect to your ${destinationName} bucket. Check the credentials and try again.`
          : detail,
        variant: 'error',
      });
      return false;
    }
  };

  const create = async (draft: SyncDraft): Promise<void> => {
    try {
      await createSync.mutateAsync({ sync: buildCreateInput(destination, draft) });
      // Navigation waits on the caller's refresh, so the config page never
      // paints its blank slate over a sync that now exists.
      onCreated();
      snackbars.create({
        message: `Your data will now export to your ${bucketLabel(draft.config)} ${destinationName} bucket`,
        variant: 'default',
      });
      void track('warehouse', 'createSyncCompleted', { Type: destinationName });
    } catch {
      snackbars.create({
        message: `We could not create your sync to ${destinationName}. Try again.`,
        variant: 'error',
      });
    }
  };

  return { validate, create, isValidating: validateConfig.isPending, isCreating: createSync.isPending };
}
